import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, LinearProgress, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  CustomTreeItem,
  TypographyNormal,
  TypographySmall,
  TypographyXSmall,
} from "../../customComponent";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { colorStyling } from "../../../helpers/color";

const GroupChildrenItem = ({
  asset,
  childAsset,
  index,
  uploadingMedia,
  removeAsset,
}) => {
  const { t } = useTranslation("cms");

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: childAsset.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      width={"100%"}
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <CustomTreeItem
        key={`${childAsset.id}`}
        itemId={`${asset.assetID}_${childAsset.id}`}
        customRootStyle={{
          sx: {
            position: "relative",
          },
        }}
        customContentStyle={{
          sx: {
            pr: 1,
            ml: 2,
            pl: 2,
          },
        }}
        label={
          <Box display={"flex"} sx={{ width: "300px" }}>
            <Tooltip
              arrow={!isDragging}
              disableInteractive
              title={
                isDragging ? (
                  ""
                ) : (
                  <TypographySmall sx={{ color: "white", textAlign: "center" }}>
                    {t("designer.contentOrderTip")}
                  </TypographySmall>
                )
              }
            >
              <Box
                minWidth={"30px"}
                display={"flex"}
                alignItems={"center"}
                {...listeners}
                {...attributes}
                sx={{ cursor: "grab" }}
              >
                <Box display={"flex"} alignItems={"center"} sx={{ mx: "4px" }}>
                  <UnfoldMoreIcon
                    sx={{
                      ":hover": {
                        border: `2px solid ${colorStyling.primary}`,
                        borderRadius: "4px",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>

            <Box display={"flex"} flexDirection={"column"} width={"100%"}>
              <Box
                width={"100%"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Box display={"flex"} gap={"4px"}>
                  {!childAsset.needUpload ? (
                    <TypographyNormal
                      display={"flex"}
                      alignItems={"center"}
                      sx={{
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {`${index + 1}.`}
                    </TypographyNormal>
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        p: "1px",
                        width: 20,
                        height: 20,
                        borderRadius: "999px",
                      }}
                    >
                      {uploadingMedia[childAsset.id]?.status === "completed" ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <Tooltip
                          arrow
                          disableInteractive
                          title={t("setting.notYetUploadedTip")}
                        >
                          <InfoIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                    </Box>
                  )}

                  <Tooltip
                    arrow
                    disableInteractive
                    title={
                      <TypographySmall
                        sx={{
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        {childAsset.name}
                      </TypographySmall>
                    }
                  >
                    <Box width={"200px"}>
                      <TypographyNormal
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {childAsset.name}
                      </TypographyNormal>
                    </Box>
                  </Tooltip>
                </Box>

                <Tooltip
                  arrow
                  disableInteractive
                  title={t("setting.removeChildrenAssetTip")}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => removeAsset(e, childAsset.id, index)}
                  >
                    <CloseIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              </Box>

              {childAsset.needUpload && (
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                  sx={{
                    ml: childAsset.needUpload ? 4 : 3,
                    mt: "-6px",
                    position: "relative",
                  }}
                >
                  <TypographyXSmall>
                    {!uploadingMedia[childAsset.id]
                      ? t("setting.saveToBeginUploadHint")
                      : uploadingMedia[childAsset.id]?.status !== "completed"
                      ? t("setting.uploadNewChildrenHint")
                      : t("setting.uploadNewSuccessHint")}
                  </TypographyXSmall>

                  {uploadingMedia[childAsset.id] && (
                    <>
                      {uploadingMedia[childAsset.id]?.status === "queued" ? (
                        <LinearProgress
                          sx={{
                            width: 60,
                            height: 5,
                            borderRadius: "11px",
                          }}
                          variant="query"
                        />
                      ) : (
                        <LinearProgress
                          sx={{
                            width: 60,
                            height: 5,
                            borderRadius: "11px",
                          }}
                          variant="determinate"
                          value={uploadingMedia[childAsset.id]?.progress ?? 77}
                        />
                      )}
                    </>
                  )}

                  {/* // TODO: handling error upon upload */}
                  {/* <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      p: "1px",
                      top: "-16px",
                      right: "-11px",
                      position: "absolute",
                      borderRadius: "999px",
                      backgroundColor: "orangered",
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </Box> */}
                </Box>
              )}
            </Box>
          </Box>
        }
      />
    </Box>
  );
};

export default GroupChildrenItem;
